import { INVALID_NOT_EMPTY, INVALID_PASSWORD_CONFIRM, INVALID_STRING_DISALLOWED_CHARACTERS } from "@/constants/invalids";
import { PATTERN } from "@/constants/pattern";
import { getDisallowedCharacters, replaceStr } from "@/utils/string";
import { validator } from "@/utils/validator";
import type { TestContext } from "yup";

// Password change is optional: the user may save only a name change.
// Touching ANY of the three password fields (current / new / confirm) is the signal that the user
// wants to change the password — as soon as one is filled, all three become required together.
// Cross-field "required" is expressed with .test() reading this.parent (not .when) so the three
// fields can depend on each other without a yup cyclic dependency.
// hasPassword drives the 現在のパスワード field:
//   - has a password -> must enter the correct current one to change it
//   - no password yet (Google-only) -> field is disabled; never required
type TPasswordSiblings = {
  currentPassword?: string | null;
  newPassword?: string | null;
  confirmNewPassword?: string | null;
};

const isAnyPasswordFilled = (parent: TPasswordSiblings) =>
  Boolean(parent.currentPassword || parent.newPassword || parent.confirmNewPassword);

// `enabled` lets 現在のパスワード opt out entirely for Google-only accounts (the field is disabled).
const requiredWhenAnyPasswordFilled = (enabled = true) => ({
  name: "requiredWhenAnyPasswordFilled",
  test(value: string | null | undefined, ctx: TestContext) {
    if (!enabled || value) return true;
    if (!isAnyPasswordFilled(ctx.parent as TPasswordSiblings)) return true;
    return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_NOT_EMPTY, { path: label }) });
  },
});

export const buildUserSettingEditFormSchema = (hasPassword: boolean) =>
  validator.object().shape({
    fullName: validator
      .string()
      .trim()
      .emptyToNull()
      .test("is-valid-name", INVALID_STRING_DISALLOWED_CHARACTERS, function (value) {
        if (!value) return true;
        const disallowedCharacters = getDisallowedCharacters(value, PATTERN.NAME_CHARACTER);
        if (disallowedCharacters.length === 0) return true;
        const { path, createError } = this;
        return createError({
          path,
          message: () => replaceStr(INVALID_STRING_DISALLOWED_CHARACTERS, { characters: disallowedCharacters.join("、") }),
        });
      })
      .max(26)
      .label("氏名")
      .required(),
    newPassword: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .strongPassword()
      .max(64)
      .label("新しいパスワード")
      .notRequired()
      .test(requiredWhenAnyPasswordFilled()),
    currentPassword: validator
      .string()
      .trim()
      .emptyToNull()
      .label("現在のパスワード")
      .notRequired()
      .test(requiredWhenAnyPasswordFilled(hasPassword)),
    confirmNewPassword: validator
      .string()
      .trim()
      .autoTransformToHalfWidth()
      .emptyToNull()
      .label("新しいパスワード（確認用）")
      .notRequired()
      .test(requiredWhenAnyPasswordFilled())
      .test("passwordConfirmMatch", INVALID_PASSWORD_CONFIRM, function (value) {
        const { newPassword } = this.parent as TPasswordSiblings;
        if (!value || !newPassword) return true;
        return value === newPassword;
      }),
  });
